/**
 * Wake helper for Render free-tier API.
 *
 * Background:
 *   When the Render dyno is asleep, requests return 503 from Render's
 *   infrastructure layer — not from our FastAPI app. Those responses have no
 *   CORS headers, so the browser blocks the response and `fetch` throws
 *   "Failed to fetch / blocked by CORS" before our app's CORS middleware
 *   ever has a chance to add the headers. This makes ALL cold-start requests
 *   look identical to a network failure.
 *
 * Strategy:
 *   1. Fire-and-forget a `mode: 'no-cors'` GET to /health. Opaque responses
 *      can't be inspected, but the request *is* sent and triggers Render to
 *      spin the dyno up. Doesn't throw on missing CORS headers.
 *   2. Poll /health with `mode: 'cors'` every 3s until success. Once the
 *      FastAPI app is up, our CORSMiddleware adds the required headers and
 *      this stops throwing.
 *   3. Time-boxed at 90s (Render free-tier cold starts can run 30–60s).
 *
 * Returns true when the API is warm; false on timeout.
 */
export async function wakeApi(
  apiBase: string,
  onProgress?: (msg: string) => void,
): Promise<boolean> {
  const url = `${apiBase.replace(/\/$/, '')}/health`;

  // Trigger dyno wake without caring about the response. Opaque responses
  // don't expose status, but Render still routes them to the app.
  fetch(url, { method: 'GET', mode: 'no-cors', cache: 'no-store' }).catch(() => {});

  const TIMEOUT_MS = 90_000;
  const POLL_MS = 3_000;
  const start = Date.now();
  let attempt = 0;

  // Quick first probe — warm dyno returns immediately.
  while (Date.now() - start < TIMEOUT_MS) {
    attempt++;
    // Generic copy that doesn't expose the underlying hosting layer. The
    // additional "(תקופת פיילוט)" note signals "this won't persist" without
    // looking like a chronic platform issue.
    onProgress?.(`מתחבר לשרת… (ניסיון ${attempt} · תקופת פיילוט)`);
    try {
      const r = await fetch(url, { method: 'GET', mode: 'cors', cache: 'no-store' });
      if (r.ok) return true;
    } catch {
      /* still cold — keep polling */
    }
    await new Promise((res) => setTimeout(res, POLL_MS));
  }
  return false;
}
